import { useMemo, useState } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import type { HomeLog } from '@racepointenergy/swc/scs/types'
import _last from 'lodash/last'
import { useIntl } from 'react-intl'

import {
  Table,
  DateCell,
  DownloadButtonCell,
  MobileSortBy,
} from 'components/Table'
import {
  HomeLogsTableInnerWrapper,
  HomeLogsTableOuterWrapper,
  HomeLogsUIDCellStyles,
} from '../styles'
import { homeLogsTableMessages } from '../messages'
import { HOME_LOGS_HEADERS } from '../consts'
import { getSortOptions } from 'components/Table/utils'
import { HomeLogsMobileBlock } from './HomeLogsMobileBlock'

interface HomeLogsTableProps {
  data: HomeLog[]
}

interface ActionsCellProps {
  log: HomeLog
}

export const ActionsCell = ({ log }: ActionsCellProps) => {
  const { url } = log
  const fileNameWithParams = _last(`${url}`.split('/'))
  const fileName = decodeURI(
    fileNameWithParams?.substring(0, fileNameWithParams.indexOf('?')) || ''
  )

  return (
    <>{fileName && <DownloadButtonCell url={`${url}`} fileName={fileName} />}</>
  )
}

function HomeLogsTable(props: HomeLogsTableProps) {
  const { formatMessage } = useIntl()
  const { data } = props

  const headers = useMemo(
    () => ({
      uid: {
        label: formatMessage(homeLogsTableMessages.uid),
        id: HOME_LOGS_HEADERS.UID,
      },
      processName: {
        label: formatMessage(homeLogsTableMessages.processName),
        id: HOME_LOGS_HEADERS.PROCESS_NAME,
      },
      softwareVersion: {
        label: formatMessage(homeLogsTableMessages.softwareVersion),
        id: HOME_LOGS_HEADERS.SOFTWARE_VERSION,
      },
      uploadTime: {
        label: formatMessage(homeLogsTableMessages.uploadTime),
        id: HOME_LOGS_HEADERS.UPLOAD_TIME,
      },
      urlInfo: {
        label: '',
        id: HOME_LOGS_HEADERS.URL_INFO,
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<HomeLog>[] = useMemo(
    () => [
      {
        id: headers.uid.id,
        Header: headers.uid.label,
        accessor: (row) => row.uid,
        cellStyles: HomeLogsUIDCellStyles,
      },
      {
        id: headers.processName.id,
        Header: headers.processName.label,
        accessor: (row) => row.processName,
      },
      {
        id: headers.softwareVersion.id,
        Header: headers.softwareVersion.label,
        accessor: (row) => row.softwareVersion,
      },
      {
        id: headers.uploadTime.id,
        Header: headers.uploadTime.label,
        accessor: (row) => row.uploadTime,
        Cell: DateCell,
      },
      {
        id: headers.urlInfo.id,
        Header: headers.urlInfo.label,
        accessor: (row) => row.urlInfo,
        Cell: ({ row: { original } }: Cell<HomeLog>) => (
          <ActionsCell log={original} />
        ),
        disableSortBy: true,
        width: 70,
      },
    ],
    [headers]
  )

  const sortOptions = useMemo(
    () =>
      getSortOptions(headers, true).filter(
        (option) => HOME_LOGS_HEADERS.URL_INFO !== option.id
      ),
    [headers]
  )
  const [sortBy, setSortBy] = useState<SortingRule<HomeLog>>(sortOptions[3])

  return (
    <>
      <MobileSortBy<HomeLog>
        headers={headers}
        sortOptions={sortOptions}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      <HomeLogsTableOuterWrapper>
        <HomeLogsTableInnerWrapper>
          <Table
            columns={columns}
            data={data}
            defaultSortBy={sortOptions[3]}
            sortBy={sortBy}
            setSortBy={setSortBy}
            mobileViewComponent={HomeLogsMobileBlock}
          />
        </HomeLogsTableInnerWrapper>
      </HomeLogsTableOuterWrapper>
    </>
  )
}

export default HomeLogsTable
